import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Search } from "lucide-react";
import { ErrorAlert, LoadingSpinner } from "../../components/ui";
import { useAuthUser } from "../../stores/authStore";
import { mensagemDeErro } from "../chamados/gestao/api";
import { useAtenderContrato, useContratosAtendidos, type ContratoAtendido } from "./api";

const normalizar = (texto: string) =>
  texto.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

/**
 * Qual contrato a equipe vai atender (/equipe/contratos). Era o cliente até
 * 09/10/2026: agora cada pessoa vê só os contratos de cuja equipe faz parte
 * (aba Operação do ProService) e que abrem chamado, e dois contratos do mesmo
 * cliente — os internos da T4i — são dois painéis. Um por vez: a escolha vira
 * uma sessão nova, escopada no contrato, e ele fica na faixa da equipe.
 */
const EscolherContrato = () => {
  const navegar = useNavigate();
  const usuario = useAuthUser();
  const contratos = useContratosAtendidos();
  const atender = useAtenderContrato();

  const [busca, setBusca] = useState("");
  const [escolhendo, setEscolhendo] = useState<number | null>(null);

  const filtrados = useMemo(() => {
    const termo = normalizar(busca);
    const todos = contratos.data ?? [];
    return termo
      ? todos.filter((c) => normalizar(`${c.cliente} ${c.contrato}`).includes(termo))
      : todos;
  }, [contratos.data, busca]);

  const escolher = async (contrato: ContratoAtendido) => {
    setEscolhendo(contrato.idContrato);
    try {
      await atender.mutateAsync(contrato.idContrato);
      // A equipe entra pelo Kanban, como o cliente (pedido de 05/10/2026).
      navegar("/");
    } catch {
      setEscolhendo(null);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Qual contrato você vai atender?</h1>
        <p className="mt-1 text-gray-600">
          Aparecem os contratos que abrem chamado e de cuja equipe você faz parte. Os
          chamados, respostas e mudanças de status valem para o contrato escolhido. Dá
          para trocar a qualquer momento pela faixa da equipe.
        </p>
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          type="search"
          value={busca}
          autoFocus
          onChange={(e) => setBusca(e.target.value)}
          onKeyDown={(e) => {
            // Enter escolhe quando a busca deixou um só.
            if (e.key === "Enter" && filtrados.length === 1) escolher(filtrados[0]);
          }}
          placeholder="Buscar cliente ou contrato"
          aria-label="Buscar cliente ou contrato"
          className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-800 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
        />
      </div>

      {atender.isError && (
        <ErrorAlert
          title="Não foi possível trocar de contrato"
          message={mensagemDeErro(atender.error, "Tente de novo.")}
          onClose={() => atender.reset()}
          variant="error"
        />
      )}

      {contratos.isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : contratos.isError ? (
        <ErrorAlert
          title="Não foi possível carregar os contratos"
          message={mensagemDeErro(contratos.error, "Tente de novo.")}
          onRetry={() => contratos.refetch()}
          variant="error"
        />
      ) : filtrados.length === 0 ? (
        <p className="rounded-2xl border border-gray-200/60 bg-white/70 p-6 text-center text-sm text-gray-500">
          {busca
            ? "Nenhum contrato com esse nome."
            : "Você não está na equipe de nenhum contrato que abre chamado. Peça para ser incluído na aba Operação do contrato, no ProService."}
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 overflow-hidden rounded-2xl border border-gray-200/60 bg-white/80">
          {filtrados.map((c) => {
            const atual = usuario?.idContrato === c.idContrato;
            return (
              <li key={c.idContrato}>
                <button
                  type="button"
                  onClick={() => escolher(c)}
                  disabled={escolhendo !== null}
                  className="flex w-full items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-blue-50/60 disabled:cursor-wait"
                >
                  <span className="min-w-0 flex-1">
                    <span
                      className={[
                        "block truncate font-medium",
                        c.abertos > 0 ? "text-gray-900" : "text-gray-600",
                      ].join(" ")}
                    >
                      {c.cliente}
                    </span>
                    <span className="block truncate text-xs text-gray-500">{c.contrato}</span>
                    {atual && (
                      <span className="text-xs font-medium text-violet-700">atendendo agora</span>
                    )}
                  </span>

                  {c.abertos > 0 ? (
                    <span className="shrink-0 rounded-full bg-orange-50 px-2.5 py-1 text-xs font-semibold text-orange-800">
                      {c.abertos} em aberto
                    </span>
                  ) : (
                    <span className="shrink-0 text-xs text-gray-400">
                      nenhum em aberto · {c.total} no total
                    </span>
                  )}

                  {escolhendo === c.idContrato ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0 text-gray-300" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default EscolherContrato;
